// Reine clientseitige Rechenlogik für PasstDasDurch.de (PRD §10/§11/§20).
// Keine React-Abhängigkeit, damit die Logik testbar bleibt (siehe scripts/calc.test.js).
// Alle Maße in Zentimetern. Bewusst vereinfachte Geometrie mit ehrlicher,
// vorsichtiger Bewertung. Treppenhaus wird nie als "passt sicher" ausgegeben.

export const STATUS = {
  FITS: "fits",
  TIGHT: "tight",
  NO: "does_not_fit",
  UNCERTAIN: "uncertain",
};

export const STATUS_LABEL = {
  fits: "Passt wahrscheinlich",
  tight: "Wird knapp",
  does_not_fit: "Passt wahrscheinlich nicht",
  uncertain: "Nur grob einschätzbar",
};

export function round1(n) {
  if (!isFinite(n)) return n;
  return Math.round(n * 10) / 10;
}

export function fmtCm(n) {
  if (!isFinite(n)) return "—";
  const r = round1(n);
  const s = (Number.isInteger(r) ? r.toString() : r.toFixed(1)).replace(".", ",");
  return `${s} cm`;
}

function toNum(v) {
  if (v === "" || v === null || v === undefined) return NaN;
  return typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
}

// Bewertet den Spielraum gegen den gewünschten Reserve-Wert (PRD §10).
// margin >= clearance: genug Reserve -> passt. 1..clearance: wird knapp.
// < 1 cm: gilt als zu knapp -> passt nicht. clearance steuert also die Strenge.
function bucketFromMargin(margin, clearance = 5) {
  const thresh = clearance > 0 ? clearance : 5;
  if (margin >= thresh) return STATUS.FITS;
  if (margin >= 1) return STATUS.TIGHT;
  return STATUS.NO;
}

// Prüft, ob ein Quader (3 Maße) durch eine rechteckige Öffnung (ow x oh) passt.
// Liefert die beste geradlinige Orientierung und eine mögliche Diagonal-Option.
function bestOpeningFit(dims, ow, oh) {
  const [a, b, c] = dims;
  const sections = [
    { cross: [a, b], through: c },
    { cross: [a, c], through: b },
    { cross: [b, c], through: a },
  ];
  const oDiag = Math.hypot(ow, oh);
  let best = null;
  let diagonal = null;
  for (const { cross: [p, q], through } of sections) {
    for (const [pw, ph] of [[p, q], [q, p]]) {
      if (pw <= ow && ph <= oh) {
        const margin = Math.min(ow - pw, oh - ph);
        if (!best || margin > best.margin) best = { margin, pw, ph, through };
      }
    }
    const long = Math.max(p, q);
    const short = Math.min(p, q);
    if (!diagonal && long <= oDiag && short <= Math.max(ow, oh)) {
      diagonal = { long, short, through };
    }
  }
  return { best, diagonal, oDiag };
}

function isValidBox(f) {
  return [f.width, f.height, f.depth].every((x) => isFinite(x) && x > 0);
}

// ----------------------------------------------------------------------------
// Tür (PRD §10 Tür-Check)
// ----------------------------------------------------------------------------
export function checkDoor(furniture, door, clearance = 3) {
  const f = { width: toNum(furniture.width), height: toNum(furniture.height), depth: toNum(furniture.depth) };
  const ow = toNum(door.width);
  const oh = toNum(door.height);
  if (!isValidBox(f) || !(ow > 0) || !(oh > 0)) {
    return { ok: false, status: STATUS.UNCERTAIN, summary: "Bitte alle Maße eingeben.", criticalMeasurements: [], possibleOrientations: [], warnings: [], tips: [] };
  }

  const { best, diagonal } = bestOpeningFit([f.width, f.height, f.depth], ow, oh);
  const warnings = [];
  const tips = [];
  const possibleOrientations = [];
  let status;
  let summary;
  const critical = [];

  if (best) {
    status = bucketFromMargin(best.margin, clearance);
    possibleOrientations.push(`Querschnitt ${fmtCm(best.pw)} x ${fmtCm(best.ph)}, ${fmtCm(best.through)} gehen durch die Tür`);
    if (status === STATUS.FITS) {
      summary = `Nach deinen Angaben passt das Möbelstück mit etwa ${fmtCm(best.margin)} Spielraum durch die Tür.`;
    } else if (status === STATUS.TIGHT) {
      summary = `Es wird knapp: nur etwa ${fmtCm(best.margin)} Spielraum am kritischen Maß.`;
      critical.push(`kritisches Maß mit ${fmtCm(best.margin)} Spielraum`);
      tips.push("Füße oder Armlehnen abnehmen, falls möglich.", "Türblatt aushängen.", "Prüfen, ob vor und hinter der Tür Platz zum Drehen ist.");
    } else {
      summary = `Der Spielraum liegt unter 1 cm. Ohne Demontage wird das wahrscheinlich nicht passen.`;
      critical.push("Spielraum unter 1 cm");
      tips.push("Teile demontieren oder einen anderen Weg prüfen.");
    }
  } else if (diagonal) {
    status = STATUS.UNCERTAIN;
    summary = "Gerade passt es nicht, diagonal eventuell. Das lässt sich nur vor Ort sicher beurteilen.";
    warnings.push("Diagonal nur möglich, wenn vor und hinter der Tür genug Platz zum Drehen ist.");
    tips.push("Vor Ort mit Pappschablone testen.", "Türblatt aushängen.");
  } else {
    status = STATUS.NO;
    summary = "Mindestens ein Maß ist größer als die Türöffnung.";
    critical.push("Möbelmaße größer als die Türöffnung");
    tips.push("Demontage prüfen oder einen anderen Transportweg wählen.");
  }

  tips.push(`Plane mindestens ${fmtCm(clearance)} Spielraum ein und miss vor dem Transport selbst nach.`);
  return { ok: true, status, summary, criticalMeasurements: critical, possibleOrientations, warnings, tips };
}

// ----------------------------------------------------------------------------
// Flur / Gang (PRD §10 Flur-Check)
// ----------------------------------------------------------------------------
export function checkHallway(furniture, hallway, clearance = 3) {
  const dims = [toNum(furniture.width), toNum(furniture.height), toNum(furniture.depth)].sort((a, b) => a - b);
  const hw = toNum(hallway.width);
  const hh = hallway.height === "" || hallway.height === undefined ? null : toNum(hallway.height);
  if (dims.some((x) => !isFinite(x) || x <= 0) || !(hw > 0)) {
    return { ok: false, status: STATUS.UNCERTAIN, summary: "Bitte Möbelmaße und Flurbreite eingeben.", criticalMeasurements: [], possibleOrientations: [], warnings: [], tips: [] };
  }
  const [s1, s2] = dims; // zwei kleinste Maße bilden den Querschnitt
  const widthMargin = hw - s1;
  const heightMargin = hh !== null ? hh - s2 : Infinity;
  const margin = Math.min(widthMargin, heightMargin);
  const status = margin < 0 ? STATUS.NO : bucketFromMargin(margin, clearance);
  const critical = [];
  if (widthMargin < 5) critical.push(`Flurbreite (${fmtCm(widthMargin)} Spielraum)`);
  if (hh !== null && heightMargin < 5) critical.push(`freie Höhe (${fmtCm(heightMargin)} Spielraum)`);
  const warnings = ["Drehungen in engen Fluren lassen sich mit diesem Rechner nur grob einschätzen."];
  const tips = [`Achte auf Platz zum Drehen vor und hinter der Engstelle.`, `Plane mindestens ${fmtCm(clearance)} Spielraum ein.`];
  const summary =
    status === STATUS.FITS
      ? `Die schmalste Möbelseite passt mit etwa ${fmtCm(widthMargin)} Spielraum in den Flur.`
      : status === STATUS.TIGHT
      ? `Es wird knapp im Flur: etwa ${fmtCm(margin)} Spielraum am kritischen Maß.`
      : "Die schmalste Möbelseite passt nicht in den Flur.";
  return { ok: true, status, summary, criticalMeasurements: critical, possibleOrientations: [], warnings, tips };
}

// ----------------------------------------------------------------------------
// Aufzug (PRD §10 Aufzug-Check)
// ----------------------------------------------------------------------------
export function checkElevator(furniture, elevator, clearance = 3) {
  const f = { width: toNum(furniture.width), height: toNum(furniture.height), depth: toNum(furniture.depth) };
  const doorWidth = toNum(elevator.doorWidth);
  const iw = toNum(elevator.innerWidth);
  const id = toNum(elevator.innerDepth);
  const ih = toNum(elevator.innerHeight);
  if (!isValidBox(f) || ![doorWidth, iw, id, ih].every((x) => isFinite(x) && x > 0)) {
    return { ok: false, status: STATUS.UNCERTAIN, summary: "Bitte Möbelmaße und alle Aufzugmaße eingeben.", criticalMeasurements: [], possibleOrientations: [], warnings: [], tips: [] };
  }

  // 1. Eintritt durch die Aufzugtür (Türbreite x Innenhöhe).
  const doorFit = bestOpeningFit([f.width, f.height, f.depth], doorWidth, ih);
  // 2. Aufstellen im Innenraum: zwei Maße auf die Grundfläche, drittes <= Innenhöhe.
  const dims = [f.width, f.height, f.depth];
  let insideFits = false;
  let insideDiagonal = false;
  const floorDiag = Math.hypot(iw, id);
  for (let i = 0; i < 3; i++) {
    const through = dims[i];
    const rest = dims.filter((_, j) => j !== i).sort((a, b) => a - b);
    if (through <= ih && rest[0] <= Math.min(iw, id) && rest[1] <= Math.max(iw, id)) insideFits = true;
    if (through <= ih && rest[1] <= floorDiag && rest[0] <= Math.min(iw, id)) insideDiagonal = true;
  }

  const critical = [];
  const warnings = [];
  const tips = [`Plane mindestens ${fmtCm(clearance)} Spielraum ein.`, "Achte auf die genaue Lage der Aufzugtür."];
  let status;
  let summary;

  if (!doorFit.best && !doorFit.diagonal) {
    status = STATUS.NO;
    summary = "Das Möbelstück passt nicht durch die Aufzugtür.";
    critical.push("Aufzugtür");
  } else if (!insideFits && !insideDiagonal) {
    status = STATUS.NO;
    summary = "Im Aufzug ist zu wenig Platz zum Aufstellen.";
    critical.push("Innenraum des Aufzugs");
  } else {
    const doorStatus = doorFit.best ? bucketFromMargin(doorFit.best.margin, clearance) : STATUS.UNCERTAIN;
    if (doorStatus === STATUS.FITS && insideFits) {
      status = STATUS.FITS;
      summary = `Eintritt und Innenraum passen, etwa ${fmtCm(doorFit.best.margin)} Spielraum an der Tür.`;
    } else if (insideDiagonal && !insideFits) {
      status = STATUS.UNCERTAIN;
      summary = "Im Aufzug passt es eventuell diagonal. Vor Ort prüfen.";
      warnings.push("Diagonal stellen nur möglich, wenn die Tür breit genug ist.");
    } else {
      status = STATUS.TIGHT;
      summary = "Es wird knapp am Aufzug, miss vor Ort genau nach.";
      if (doorFit.best && doorFit.best.margin < 5) critical.push(`Türbreite (${fmtCm(doorFit.best.margin)} Spielraum)`);
    }
  }
  return { ok: true, status, summary, criticalMeasurements: critical, possibleOrientations: [], warnings, tips };
}

// ----------------------------------------------------------------------------
// Treppenhaus (PRD §10 Treppenhaus-Check) — bewusst vorsichtig, nie "passt sicher".
// ----------------------------------------------------------------------------
export function checkStaircase(furniture, staircase, clearance = 3) {
  const dims = [toNum(furniture.width), toNum(furniture.height), toNum(furniture.depth)].sort((a, b) => a - b);
  const stairWidth = toNum(staircase.stairWidth);
  const landingWidth = toNum(staircase.landingWidth);
  const landingDepth = toNum(staircase.landingDepth);
  const clearHeight = staircase.clearHeight === "" || staircase.clearHeight === undefined ? null : toNum(staircase.clearHeight);
  if (dims.some((x) => !isFinite(x) || x <= 0) || ![stairWidth, landingWidth, landingDepth].every((x) => isFinite(x) && x > 0)) {
    return { ok: false, status: STATUS.UNCERTAIN, summary: "Bitte Möbelmaße und Treppenhausmaße eingeben.", criticalMeasurements: [], possibleOrientations: [], warnings: [], tips: [] };
  }
  const [s1, s2, s3] = dims; // klein -> groß
  const landingDiag = Math.hypot(landingWidth, landingDepth);
  const critical = [];

  const fitsStairWidth = s1 <= stairWidth;
  const fitsHeight = clearHeight === null ? true : s2 <= clearHeight;
  const fitsLanding = s3 <= landingDiag;

  let status;
  let summary;
  let warningsExtra = null;
  if (!fitsStairWidth || !fitsHeight) {
    // Hartes Nein nur bei zu schmaler Treppe oder zu geringer lichter Höhe.
    status = STATUS.NO;
    summary = "Nach den Maßen wird das Treppenhaus ohne Demontage wahrscheinlich nicht machbar sein.";
    if (!fitsStairWidth) critical.push("Treppenbreite");
    if (!fitsHeight) critical.push("lichte Höhe");
  } else {
    // Bestes Ergebnis bleibt "nur grob einschätzbar" — echte Treppenhäuser sind komplex.
    status = STATUS.UNCERTAIN;
    summary = "Nach den eingegebenen Maßen sieht es wahrscheinlich machbar aus. Prüfe vor Ort besonders Podeste, Geländer und Türrahmen.";
    if (!fitsLanding) {
      critical.push("Podestdrehung knapp");
      warningsExtra = "Das Podest ist klein zum Drehen. Hier entscheidet sich vor Ort, ob es geht.";
    }
  }

  const warnings = [
    "Treppenhäuser lassen sich nur grob berechnen. Kurven, Geländer und Türrahmen entscheiden vor Ort.",
  ];
  if (warningsExtra) warnings.push(warningsExtra);
  const tips = [
    "Miss Podeste, Geländerabstand und Türrahmen am Treppenhaus genau nach.",
    `Plane mindestens ${fmtCm(clearance)} Spielraum ein.`,
    "Wenn möglich, Teile demontieren.",
  ];
  return { ok: true, status, summary, criticalMeasurements: critical, possibleOrientations: [], warnings, tips };
}

// Dispatcher für die UI.
export function checkFit(obstacleType, furniture, obstacle, clearance = 3) {
  switch (obstacleType) {
    case "door":
      return checkDoor(furniture, obstacle, clearance);
    case "hallway":
      return checkHallway(furniture, obstacle, clearance);
    case "elevator":
      return checkElevator(furniture, obstacle, clearance);
    case "staircase":
      return checkStaircase(furniture, obstacle, clearance);
    default:
      return { ok: false, status: STATUS.UNCERTAIN, summary: "Unbekannte Engstelle.", criticalMeasurements: [], possibleOrientations: [], warnings: [], tips: [] };
  }
}
